import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { supabase } from '@/api/supabase';
import { useAuth } from '@/hooks/useAuth';

interface FavoritesContextValue {
  favoriteIds: Set<string>;
  toggle: (propertyId: string) => Promise<void>;
  ready: boolean;
}

const FavoritesContext = createContext<FavoritesContextValue>({
  favoriteIds: new Set(),
  toggle: async () => {},
  ready: false,
});

export function FavoritesProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!user) {
      setFavoriteIds(new Set());
      setReady(true);
      return;
    }
    setReady(false);
    supabase
      .from('favorites')
      .select('property_id')
      .eq('user_id', user.id)
      .then(({ data }) => {
        if (data) setFavoriteIds(new Set(data.map((f: { property_id: string }) => f.property_id)));
        setReady(true);
      });
  }, [user?.id]);

  const toggle = useCallback(async (propertyId: string) => {
    if (!user) return;
    setFavoriteIds(prev => {
      const next = new Set(prev);
      if (next.has(propertyId)) next.delete(propertyId);
      else next.add(propertyId);
      return next;
    });
    const isFav = favoriteIds.has(propertyId);
    if (isFav) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (supabase.from('favorites') as any)
        .delete()
        .eq('user_id', user.id)
        .eq('property_id', propertyId);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (supabase.from('favorites') as any)
        .insert({ user_id: user.id, property_id: propertyId });
    }
  }, [user, favoriteIds]);

  return (
    <FavoritesContext.Provider value={{ favoriteIds, toggle, ready }}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  return useContext(FavoritesContext);
}
